<template>
  <div class="flex pb-6 justify-center items-center flex-col md:flex-row md:justify-between gap-4">
    <div class="w-full md:w-auto flex justify-center items-center gap-12 md:gap-4">
      <div v-if="canViewRequestHistoryReport" class="flex flex-col gap-2">
        <!-- Histórico por fecha de entrada, como en contratos (ADS-0082): obligatoria
             y de un año como máximo (ADS-0186). -->
        <exportFetch export-endpoint="/request/user/export"
          :file-name="$t('requests.fileName')"
          date-range
          :from-label="$t('requests.contractExport.dateFrom')"
          :to-label="$t('requests.contractExport.dateTo')"
          @export-started="() => console.log($t('requests.logs.exportStarted'))"
          @export-completed="() => console.log($t('requests.logs.exportCompleted'))"
          @export-failed="() => console.log($t('requests.logs.exportFailed'))" />
      </div>

      <div class="relative inline-block" ref="contractExportWrapper">
        <ButtonWhite class="flex items-center gap-2" :disabled="isExportingContracts" @click="toggleContractExportMenu">
          {{ isExportingContracts ? $t('requests.contractExport.exporting') : $t('requests.contractExport.button') }}
        </ButtonWhite>

        <transition name="fade">
          <div
            v-if="showContractExportMenu"
            class="absolute left-0 z-50 mt-2 w-80 rounded-lg border border-[#7CADB9] bg-white p-4 text-left shadow-lg"
          >
            <div class="grid gap-3">
              <label class="flex flex-col gap-1 text-sm text-[#385660]">
                <span>{{ $t('requests.contractExport.dateFrom') }}</span>
                <input
                  v-model="contractDateFrom"
                  type="date"
                  :max="contractDateTo || undefined"
                  :disabled="isExportingContracts"
                  class="rounded-md border border-gray-300 px-3 py-2 text-[#385660]"
                >
              </label>
              <label class="flex flex-col gap-1 text-sm text-[#385660]">
                <span>{{ $t('requests.contractExport.dateTo') }}</span>
                <input
                  v-model="contractDateTo"
                  type="date"
                  :min="contractDateFrom || undefined"
                  :disabled="isExportingContracts"
                  class="rounded-md border border-gray-300 px-3 py-2 text-[#385660]"
                >
              </label>
            </div>

            <p v-if="contractExportError" class="mt-3 text-xs text-red-600" role="alert">
              {{ contractExportError }}
            </p>
            <p v-else-if="isExportingContracts" class="mt-3 text-xs text-[#7CADB9]">
              {{ $t('requests.contractExport.processing', { processed: contractRowsProcessed, total: contractRowsTotal }) }}
            </p>

            <div class="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                class="rounded-md bg-[#2AB4D9] px-3 py-2 text-sm text-white hover:bg-[#229abb] disabled:opacity-50"
                :disabled="isExportingContracts"
                @click="exportContractsReport"
              >
                {{ $t('requests.contractExport.download') }}
              </button>
            </div>
          </div>
        </transition>
      </div>

      <PrintTableComponent v-if="printableItems.length" :title="$t('requests.title')" :fields="fields" :items="printableItems"
        @print-started="() => console.log($t('requests.logs.printStarted'))"
        @print-completed="() => console.log($t('requests.logs.printCompleted'))"
        @print-failed="(error) => console.error($t('requests.logs.printFailed'), error)" />

      <!-- Componente Nuevo Solicitud con Dropdown -->
      <NewRequestDropdown />
    </div>
  </div>

  <TitlePrimary :title="pageTitle" class="w-full" />

  <div v-if="route.query && route.query.id" class="mt-2 flex items-center gap-2">
    <ButtonWhite class="flex items-center gap-2" @click="removeIdParam">{{ $t('common.backToSearch')}}</ButtonWhite>
    <div class="text-sm text-[#385660]">{{ noItemsForAccount }}</div>
  </div>

  <div class="pt-4 px-2 overflow-x-auto">
    <!-- Mobile -->
    <div class="md:hidden">
      <CardTable v-if="role == 'superadmin'" :fields="fields" :items="items.length > 0 ? [items[currentItemIndex]] : []"
        :showOptions="true" route="/request-detail-superadmin" />
      <CardTable v-else :fields="fields" :items="items.length > 0 ? [items[currentItemIndex]] : []" :showOptions="false"
        route="/request-detail" />
      <MobilePaginateComponent v-if="items.length > 0" :totalPages="totalPages" :modelValue="currentPage"
        :totalItems="totalItems" :currentItemIndex="currentItemIndex" :itemsPerPage="10"
        @update:modelValue="handlePageChange" @update:currentItemIndex="handleItemChange" />
    </div>
    <!-- Desktop -->
    <div class="hidden md:block">
      <RequestTableComponent v-if="role == 'superadmin'" :fields="fields" :items="items" :showOptions="true"
        :filter-definitions="filterDefinitions" :filter-error="filterError" v-model:filters="filters" @filter-change="onFilterChange"
        route="/request-detail-superadmin" />
      <RequestTableComponent v-else :fields="fields" :items="items" :showOptions="false" route="/request-detail"
        :filter-definitions="filterDefinitions" :filter-error="filterError" v-model:filters="filters" @filter-change="onFilterChange" />
    </div>
    <!-- Desktop -->
    <div class="hidden md:block">
      <PaginateComponent :totalPages="totalPages" :paginationInfo="paginationInfo" :modelValue="currentPage" @update:modelValue="handlePageChange" />
    </div>
  </div>

</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import axios from "axios";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';

import TitlePrimary from "@/components/title/title-primary.vue";
import RequestTableComponent from "@/components/table/RequestTableComponent.vue";
import { useTableFilters } from "@/composables/useTableFilters";
import { isAdsUser } from "@/services/userRoles";
import { defaultExportRange, exportRangeError } from "@/services/exportDateRange";
import PaginateComponent from "@/components/paginate/PaginateComponent.vue";
import { usePaginationInfo } from "@/composables/usePaginationInfo";
import PrintTableComponent from "@/components/print/PrintTableComponent.vue";
import exportFetch from "@/components/fetch/exportFetch.vue";
import CardTable from "@/components/table/CardTable.vue";
import MobilePaginateComponent from "@/components/paginate/MobilePaginateComponent.vue";
import NewRequestDropdown from "@/components/button/NewRequestDropdown.vue";
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import { canAccessRequestHistoryReport } from '@/services/requestHistoryReportAccess';
import { fetchRequestManagers } from '@/services/requestManagers';
import { hasAnyRole } from '@/services/userRoles';

const { t } = useI18n();

// La columna Administrador (administrador de fincas / API / inmobiliaria) solo
// tiene sentido para los perfiles ADS, que ven solicitudes de muchas cuentas.
// Cada cliente trabaja dentro de su propia cuenta y no la necesita.
const fields = [
  { key: "code", label: "Ref." },
  { key: "created_at", label: "Fecha" },
  { key: "type", label: "Tipología" },
  ...(isAdsUser() ? [{ key: "contract_improvement", label: t('contractImprovement.listColumn') }] : []),
  ...(isAdsUser() || hasAnyRole(['admin']) ? [{ key: "manager", label: t('requestHistory.fields.manager') }] : []),
  { key: "supply_address", label: "Dirección de Suministro" },
  { key: "cups", label: "CUPS" },
  { key: "assigned_to", label: "Gestor" },
  { key: "status", label: "Estado" },
];

// --- Estado ---
const items = ref([]);
const printableItems = computed(() => {
  if (!hasAnyRole(['admin']) || hasAnyRole(['superadmin'])) return items.value;
  return items.value.filter((item) => Number(item.assigned_to_id) === Number(localStorage.getItem('id')));
});
const currentPage = ref(1);
const totalPages = ref(1);
const { paginationInfo, setPaginationInfo } = usePaginationInfo();
const isSearchActive = ref(false);
const mobile = ref('desktop');
// const loading = ref(true);
const selectedStatus = ref('');
const totalItems = ref(0);
const currentItemIndex = ref(0); // Add this to track current item in the page

const role = ref(localStorage.getItem('role'));
const route = useRoute();
const router = useRouter();
const noItemsForAccount = ref('');

// Las secciones Luz y Gas son este mismo listado acotado a un suministro
// (`?supply=light|gas`), para el cliente y para ADS (ADSREC-0006). La API decide
// qué solicitud es de cada tipo por el CUPS que informa; cualquier otro valor se
// ignora y se lista todo.
const SUPPLY_TYPES = ['light', 'gas'];
const supplyType = computed(() => (SUPPLY_TYPES.includes(route.query?.supply) ? route.query.supply : null));
// El Inicio de Back Office abre cada estado con sus propias solicitudes
// (`?mine=1`); el alcance se mantiene al filtrar y al paginar (ADS-0113).
const onlyMine = computed(() => ['1', 'true'].includes(String(route.query?.mine ?? '')));
// ADS ve las solicitudes de todas las cuentas: su título no dice "tus".
const supplyTitlesKey = isAdsUser() ? 'requests.adsSupplyTitles' : 'requests.supplyTitles';
const pageTitle = computed(() => (supplyType.value ? t(`${supplyTitlesKey}.${supplyType.value}`) : t('requests.title')));
const canViewRequestHistoryReport = ref(false);
const managers = ref([]);
const showContractExportMenu = ref(false);
const isExportingContracts = ref(false);
// ADS exporta contratos de un año como máximo y arranca con los últimos
// doce meses; el cliente sigue eligiendo las dos fechas (ADS-0186).
const contractRange = isAdsUser() ? defaultExportRange() : { from: '', to: '' };
const contractDateFrom = ref(contractRange.from);
const contractDateTo = ref(contractRange.to);
const contractExportError = ref('');
const contractRowsTotal = ref(0);
const contractRowsProcessed = ref(0);
let contractExportPolling;

const loadManagers = async () => {
  try {
    managers.value = await fetchRequestManagers();
  } catch (error) {
    console.error('No se pudieron cargar los gestores del filtro', error);
    managers.value = [];
  }
};

const loadRequestHistoryReportAccess = async () => {
  try {
    canViewRequestHistoryReport.value = await canAccessRequestHistoryReport();
  } catch (error) {
    console.error('No se pudo comprobar el acceso al histórico general de solicitudes', error);
    canViewRequestHistoryReport.value = false;
  }
};

const stopContractExportPolling = () => {
  if (contractExportPolling) {
    clearInterval(contractExportPolling);
    contractExportPolling = undefined;
  }
};

const toggleContractExportMenu = () => {
  if (isExportingContracts.value) return;
  contractExportError.value = '';
  showContractExportMenu.value = !showContractExportMenu.value;
};

const downloadContractsReport = async (filePath) => {
  const response = await axios.get(
    `${import.meta.env.VITE_API_URL}/export/download`,
    {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      params: { path: filePath },
      responseType: 'blob',
    }
  );

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = `${t('requests.contractExport.fileName')}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(link.href);
};

const exportContractsReport = async () => {
  if (isExportingContracts.value) return;

  // El informe de contratos se exporta solo por rango de fechas: el botón
  // "Informe completo" (sin acotar) se retiró en la validación del 15/09.
  if (!contractDateFrom.value || !contractDateTo.value) {
    contractExportError.value = t('requests.contractExport.dateRangeRequired');
    return;
  }

  if (contractDateFrom.value > contractDateTo.value) {
    contractExportError.value = t('requests.contractExport.invalidDateRange');
    return;
  }

  if (isAdsUser() && exportRangeError(contractDateFrom.value, contractDateTo.value) === 'tooLong') {
    contractExportError.value = t('export.range.tooLong');
    return;
  }

  isExportingContracts.value = true;
  contractExportError.value = '';
  contractRowsTotal.value = 0;
  contractRowsProcessed.value = 0;
  stopContractExportPolling();

  try {
    const exportResponse = await axios.get(
      `${import.meta.env.VITE_API_URL}/request/user/export/contracts`,
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        params: {
          file_type: 'xlsx',
          ...(contractDateFrom.value ? { date_from: contractDateFrom.value } : {}),
          ...(contractDateTo.value ? { date_to: contractDateTo.value } : {}),
        },
      }
    );

    const jobId = exportResponse.data?.value?.job_id;
    if (!jobId) throw new Error(t('requests.contractExport.noJobId'));

    const checkStatus = async () => {
      try {
        const statusResponse = await axios.get(
          `${import.meta.env.VITE_API_URL}/export/status`,
          {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            params: { job_id: jobId },
          }
        );

        const job = statusResponse.data?.value;
        contractRowsTotal.value = Number(job?.rows_total) || 0;
        contractRowsProcessed.value = Number(job?.rows_processed) || 0;

        if (job?.status === 'finished') {
          if (!job.file_path) throw new Error(t('requests.contractExport.noFile'));
          stopContractExportPolling();
          await downloadContractsReport(job.file_path);
          showContractExportMenu.value = false;
          isExportingContracts.value = false;
        } else if (job?.status === 'failed') {
          throw new Error(t('requests.contractExport.failed'));
        }
      } catch (error) {
        stopContractExportPolling();
        contractExportError.value = error.message || t('requests.contractExport.failed');
        isExportingContracts.value = false;
      }
    };

    contractExportPolling = setInterval(checkStatus, 2000);
    await checkStatus();
  } catch (error) {
    stopContractExportPolling();
    contractExportError.value = error.message || t('requests.contractExport.failed');
    isExportingContracts.value = false;
  }
};

const formatRequestDate = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '-';
};

const mapRequestItem = (item) => ({
  ...item,
  assigned_to_id: Array.isArray(item.assigned_to) ? item.assigned_to[0]?.id : item.assigned_to?.id,
  created_at: formatRequestDate(item.date || item.created_at),
  manager: item.client_account?.name || item.manager?.name || '-',
  supply_address: item.supply_address
    || item.supplies?.find((supply) => supply?.address)?.address
    || item.property?.address
    || item.property?.name
    || item.user_address
    || '-',
  assigned_to: Array.isArray(item.assigned_to)
    ? item.assigned_to[0]?.name || '-'
    : item.assigned_to?.name || '-'
});
// --- filtros de cabecera ---
// Tipología y Estado son conjuntos cerrados: el desplegable envía la clave que
// guarda la base (other_type, results...), no la etiqueta traducida.
// Retirada «Cambio de comercializadora» (ADS-0130): se deja comentada, no borrada, por si hubiera que reponerla.
// const REQUEST_TYPES = ['supplies', 'invoice', 'change_ownership', 'change_supplier', 'account_change', 'other_type'];
const REQUEST_TYPES = ['supplies', 'invoice', 'change_ownership', 'account_change', 'other_type'];
const REQUEST_STATUSES = ['created', 'review', 'pending', 'results', 'cancel'];

const filterDefinitions = computed(() => [
  { key: 'code', type: 'text' },
  { key: 'created_at', type: 'date' },
  {
    key: 'type',
    type: 'select',
    options: REQUEST_TYPES.map((value) => ({ value, label: t(`common.requestTypes.${value}`) })),
  },
  ...(isAdsUser() ? [{
    key: 'contract_improvement',
    type: 'select',
    options: [
      { value: 'accepted', label: t('contractImprovement.filter.accepted') },
      { value: 'not_accepted', label: t('contractImprovement.filter.notAccepted') },
    ],
  }] : []),
  ...(isAdsUser() || hasAnyRole(['admin']) ? [{ key: 'manager', type: 'text' }] : []),
  { key: 'supply_address', type: 'text' },
  { key: 'cups', type: 'text' },
  // Gestor: desplegable con los gestores que la API deja ver a este perfil. Si
  // no hay ninguno —o falla la carga— se mantiene el texto libre para no dejar
  // la columna sin filtro.
  managers.value.length
    ? {
        key: 'assigned_to',
        type: 'select',
        options: managers.value.map((manager) => ({ value: String(manager.id), label: manager.name })),
      }
    : { key: 'assigned_to', type: 'text' },
  {
    key: 'status',
    type: 'select',
    options: REQUEST_STATUSES.map((value) => ({ value, label: t(`common.requestStatus.${value}`) })),
  },
]);

const { filters, isFiltering, filterError, runSearch, onFilterChange } = useTableFilters({
  endpoint: () => (route.query?.id || role.value !== 'superadmin' ? '/request/user/search' : '/request/superadmin/search'),
  perPage: 10,
  translate: t,
  extraParams: () => ({
    ...(route.query?.id ? { id: route.query.id } : {}),
    ...(supplyType.value ? { supply_type: supplyType.value } : {}),
    ...(onlyMine.value ? { mine: 1 } : {}),
    ...(REQUEST_STATUSES.includes(route.query.status) ? { 'filters[status]': route.query.status } : {}),
  }),
  onResults: (data) => {
    const page = data?.value;
    if (!page) return;
    items.value = (page.data || []).map(mapRequestItem);
    totalPages.value = page.last_page || 1;
    setPaginationInfo(page);
    currentPage.value = page.current_page || 1;
    totalItems.value = page.total ?? items.value.length;
    currentItemIndex.value = 0;
    isSearchActive.value = true;
  },
  onCleared: () => {
    isSearchActive.value = false;
    currentPage.value = 1;
    currentItemIndex.value = 0;
    fetchDataFromApi();
  },
});

// --- funciones ---
const fetchDataFromApi = async () => {
  isSearchActive.value = false;
  try {
    // Si viene id por query, forzar endpoint de usuario
    const endpoint = route.query && route.query.id
      ? '/request/user/list'
      : (role.value == 'superadmin' ? '/request/superadmin/list' : '/request/user/list');

    const params = {
      per_page: 10,
      page: currentPage.value,
      status: route.query.status || selectedStatus.value
    };
    if (route.query && route.query.id) params.id = route.query.id;
    if (supplyType.value) params.supply_type = supplyType.value;
    if (onlyMine.value) params.mine = 1;

    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}${endpoint}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params
      }
    );
    console.log(response.data.value);
    if (response.data?.value.data) {
      response.data.value.data = response.data.value.data.map(mapRequestItem);

      console.log("axios", response.data.value.data);
      items.value = response.data.value.data;
      totalPages.value = response.data.value.last_page;
      setPaginationInfo(response.data.value);
      totalItems.value = response.data.value.total;
      console.log(items);
      noItemsForAccount.value = '';
    }
  } catch (error) {
    items.value = [];
    if (error.response && error.response.status === 404) {
      noItemsForAccount.value = 'No hay resultados para esta cuenta';
    } else {
      console.error("Error fetching data:", error);
    }
  }
};

function removeIdParam() {
  const newQuery = { ...route.query };
  if (newQuery && newQuery.id) {
    delete newQuery.id;
    router.replace({ path: route.path, query: newQuery }).catch(() => {});
  }
  noItemsForAccount.value = '';
  fetchDataFromApi();
}

const handleItemChange = (newIndex) => {
  if (newIndex >= 0 && newIndex < items.value.length) {
    currentItemIndex.value = newIndex;
  }
};

const handlePageChange = async (newPage) => {
  currentPage.value = newPage;
  currentItemIndex.value = 0; // Reset to first item when changing pages
  if (isFiltering.value) {
    await runSearch(newPage);
  } else {
    await fetchDataFromApi();
  }
};

// Pasar de Luz a Gas (o a Solicitudes) reutiliza este componente: se vuelve a
// la primera página y se relanza la consulta que estuviera activa.
watch([supplyType, onlyMine, () => route.query.status], () => {
  currentPage.value = 1;
  currentItemIndex.value = 0;
  if (isFiltering.value) {
    runSearch(1);
  } else {
    fetchDataFromApi();
  }
});

// Add a watch to reset currentItemIndex when items change
watch(items, (newItems) => {
  if (newItems.length > 0) {
    currentItemIndex.value = Math.min(currentItemIndex.value, newItems.length - 1);
  } else {
    currentItemIndex.value = 0;
  }
}, { immediate: true });


// Add mobile detection
const checkMobile = () => {
  mobile.value = window.innerWidth < 768 ? 'mobile' : 'desktop';
};

onMounted(() => {
  fetchDataFromApi();
  loadRequestHistoryReportAccess();
  loadManagers();
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  stopContractExportPolling();
  window.removeEventListener('resize', checkMobile);
});


</script>


<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
